/**
 * Shared render utilities for content modals
 * These functions handle rendering of video/image previews and related UI elements
 */

import React from 'react';
import { type UserVideo } from '@/api/videoApi';
import { UserImage } from '@/api/imageApi';
import { noOutlineHandlers, getStatusBadgeClasses } from './modalUtils';

/**
 * Editable Title Component
 * Renders an editable title with click/keyboard support
 */
export const EditableTitleInput: React.FC<{
  isEditing: boolean;
  value: string;
  displayTitle: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onStartEdit: () => void;
  canEdit?: boolean;
  onEditDenied?: () => void;
}> = ({ isEditing, value, displayTitle, onChange, onSave, onCancel, onStartEdit, canEdit = true, onEditDenied }) => {
  const handleStartEdit = () => {
    if (!canEdit && onEditDenied) {
      onEditDenied();
      return;
    }
    if (canEdit) {
      onStartEdit();
    }
  };

  if (isEditing) {
    if (!canEdit && onEditDenied) {
      // If user somehow got into editing mode without permission, show error and exit
      onEditDenied();
      return (
        <h3 className="text-xl font-bold text-gray-900">
          {displayTitle}
        </h3>
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave();
          else if (e.key === 'Escape') onCancel();
        }}
        onBlur={onSave}
        autoFocus
        className="text-xl font-bold text-gray-900 border-b-2 border-blue-500 focus:outline-none bg-transparent"
        style={{ fontFamily: 'Maven Pro', minWidth: '200px' }}
        placeholder="Enter title"
      />
    );
  }

  return (
    <button
      type="button"
      className="text-xl font-bold text-gray-900 cursor-pointer transition-colors text-left bg-transparent border-none p-0 hover:text-gray-700"
      onClick={handleStartEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStartEdit();
        }
      }}
      title={canEdit ? "Click to edit title" : "Cannot edit other users' titles"}
      style={{ fontFamily: 'Maven Pro' }}
    >
      {displayTitle}
    </button>
  );
};

/**
 * Shared info field styles for consistent rendering
 */
const infoFieldStyles = {
  label: { fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '18px', lineHeight: '26px', letterSpacing: '0%' } as const,
  value: { fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '18px', lineHeight: '26px', letterSpacing: '0%' } as const,
};

/**
 * Renders an info field with label and value
 */
const InfoField: React.FC<{
  label: string;
  value: string | React.ReactNode;
  truncate?: boolean;
  title?: string;
}> = ({ label, value, truncate = false, title }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleClick = () => {
    if (truncate && title && window.innerWidth < 768) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    }
  };

  return (
    <div className="relative">
      <span className="block text-gray-600 font-medium mb-1" style={infoFieldStyles.label}>{label}</span>
      {truncate && title ? (
        <>
          <button
            type="button"
            className={`text-gray-900 text-left truncate max-w-[120px] md:max-w-none md:cursor-default cursor-pointer bg-transparent border-none p-0 m-0`}
            style={infoFieldStyles.value}
            title={title}
            onClick={handleClick}
          >
            {value}
          </button>
          {showTooltip && (
            <div 
              className="absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-lg"
              style={{
                top: '100%',
                left: '0',
                marginTop: '4px',
                maxWidth: '250px',
                wordBreak: 'break-all'
              }}
            >
              {title}
            </div>
          )}
        </>
      ) : (
        <p 
          className="text-gray-900"
          style={infoFieldStyles.value}
        >
          {value}
        </p>
      )}
    </div>
  );
};

export const renderInfoField = (
  label: string,
  value: string | React.ReactNode,
  truncate: boolean = false,
  title?: string
): React.ReactElement => {
  return <InfoField label={label} value={value} truncate={truncate} title={title} />;
};

/**
 * Status Radio Button Component for admin status selection
 */
export const StatusRadioButton: React.FC<{
  value: string;
  currentStatus: string;
  label: string;
  onChange: (value: string) => void;
}> = ({ value, currentStatus, label, onChange }) => {
  const isSelected = currentStatus === value;
  
  return (
    <label 
      className="flex items-center gap-2 cursor-pointer transition-all"
      style={{
        width: '146px',
        height: '41px',
        borderRadius: '30px',
        paddingTop: '10px',
        paddingRight: '22px',
        paddingBottom: '10px',
        paddingLeft: '22px',
        gap: '10px',
        backgroundColor: '#ffffff',
        border: '3px solid transparent',
        backgroundImage: isSelected ? 'linear-gradient(white, white), linear-gradient(90deg, #F69A7E 0%, #939BEC 100%)' : 'none',
        backgroundOrigin: 'border-box',
        backgroundClip: isSelected ? 'padding-box, border-box' : 'padding-box',
      }}
    >
      <input
        type="radio"
        name="status"
        value={value}
        checked={isSelected}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer"
        style={{ 
          appearance: 'none',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: isSelected ? '4px solid transparent' : '2px solid #d1d5db',
          backgroundColor: '#ffffff',
          flexShrink: 0,
          backgroundImage: isSelected 
            ? 'linear-gradient(white, white), linear-gradient(90deg, #F69A7E 0%, #939BEC 100%)'
            : 'none',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      />
      <span style={{ fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '18px', lineHeight: '26px', letterSpacing: '0%', color: '#1f2937' }}>
        {label}
      </span>
    </label>
  );
};

/**
 * Renders a loading spinner icon
 */
export const renderLoadingSpinner = (color: 'gray' | 'white' = 'gray'): React.ReactElement => {
  const colorClass = color === 'white' ? 'text-white' : 'text-gray-600';
  
  return (
    <svg
      className={`animate-spin h-5 w-5 ${colorClass}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

/**
 * Renders the modal header with title, badges, and close button
 * Used by both VideoDetailsModal and VideoEditModal
 * Supports both static and editable titles
 */
export const renderModalHeader = (
  title: string,
  videoTypeLabel: string | null | undefined,
  status: string | undefined,
  onClose: () => void,
  editableTitle?: {
    isEditing: boolean;
    editedValue: string;
    canEdit: boolean;
    onStartEdit: () => void;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
    onEditDenied?: () => void;
  }
): React.ReactElement => {
  return (
    <div className="px-6 pt-6 pb-4 md:pb-4">
      {/* Desktop: Title and Badges in same row with close button */}
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          {editableTitle ? (
            <EditableTitleInput
              isEditing={editableTitle.isEditing}
              value={editableTitle.editedValue}
              displayTitle={title}
              onChange={editableTitle.onChange}
              onSave={editableTitle.onSave}
              onCancel={editableTitle.onCancel}
              onStartEdit={editableTitle.onStartEdit}
              canEdit={editableTitle.canEdit}
              onEditDenied={editableTitle.onEditDenied}
            />
          ) : (
            <h3 className="text-xl font-bold text-gray-900">
              {title}
            </h3>
          )}
          
          {videoTypeLabel && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {videoTypeLabel}
            </span>
          )}
          {status && (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(status)}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          )}
        </div>
        
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          style={{ outline: 'none', boxShadow: 'none', border: 'none', background: 'transparent' }}
          {...noOutlineHandlers}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Mobile Layout: Only Title and Close Button */}
      <div className="md:hidden flex items-center justify-between">
        <div className="flex-1 pr-2">
          {editableTitle ? (
            <EditableTitleInput
              isEditing={editableTitle.isEditing}
              value={editableTitle.editedValue}
              displayTitle={title}
              onChange={editableTitle.onChange}
              onSave={editableTitle.onSave}
              onCancel={editableTitle.onCancel}
              onStartEdit={editableTitle.onStartEdit}
              canEdit={editableTitle.canEdit}
              onEditDenied={editableTitle.onEditDenied}
            />
          ) : (
            <h3 className="text-xl font-bold text-gray-900">
              {title}
            </h3>
          )}
        </div>
        
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          style={{ outline: 'none', boxShadow: 'none', border: 'none', background: 'transparent' }}
          {...noOutlineHandlers}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/**
 * Renders type and status badges for mobile view (separate from header)
 * Used below the header in mobile layout
 */
export const renderMobileBadges = (
  videoTypeLabel: string | null | undefined,
  status: string | undefined
): React.ReactElement | null => {
  if (!videoTypeLabel && !status) return null;
  
  return (
    <div className="md:hidden grid grid-cols-2 gap-x-12 gap-y-4 w-full">
      {videoTypeLabel && renderInfoField('Type', videoTypeLabel)}
      {status && renderInfoField('Status', status.charAt(0).toUpperCase() + status.slice(1))}
    </div>
  );
};

/**
 * Renders the content preview (video or image) with appropriate loading states
 * Used by both VideoDetailsModal and VideoEditModal
 */
export const renderContentPreview = (
  isVideoContent: boolean,
  selectedVideo: UserVideo | null | undefined,
  isImageContent: boolean,
  selectedImage: UserImage | null | undefined
): React.ReactElement | null => {
  if (isVideoContent && selectedVideo) {
    // Show loading state if video URL is not available yet
    const hasVideoUrl = selectedVideo.video_url && selectedVideo.video_url !== '';
    
    if (!hasVideoUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div>
            <p className="text-white text-sm">Loading video...</p>
          </div>
        </div>
      );
    }
    
    return (
      <video
        key={selectedVideo.video_url}
        className="w-full h-full bg-black"
        style={{ 
          objectFit: 'contain',
          display: 'block'
        }}
        controls
        autoPlay
        muted
        poster={selectedVideo.thumbnail_url}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          const isActuallyPortrait = video.videoHeight > video.videoWidth;
          
          // Force re-render of parent container if needed
          if (isActuallyPortrait && video.parentElement) {
            video.parentElement.style.maxWidth = '300px';
            video.parentElement.style.margin = '0 auto';
            video.parentElement.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
          }
        }}
      >
        <source src={selectedVideo.video_url} type="video/mp4" />
        <track
          kind="captions"
          srcLang="en"
          label="English captions"
          default
        />
        Your browser does not support the video tag.
      </video>
    );
  }
  
  if (isImageContent && selectedImage) {
    return (
      <img
        src={selectedImage.image_url}
        alt={selectedImage.title || 'Generated Image'}
        className="max-w-full max-h-full object-contain"
      />
    );
  }
  
  return null;
};

/**
 * Renders the prompt information section with copy button
 * Used by both VideoDetailsModal and VideoEditModal
 */
export const renderPromptInfo = (
  prompt: string | undefined,
  showCopiedMessage: boolean,
  handleCopyPrompt: () => void
): React.ReactElement | null => {
  if (!prompt) return null;

  return (
    <div>
      <span className="block text-gray-600 font-medium mb-1" style={{ fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '18px', lineHeight: '26px', letterSpacing: '0%' }}>
        Generation Prompt
      </span>
      <div className="relative rounded-3xl border border-gray-200" style={{ backgroundColor: '#F0F7FF' }}>
        <div className="flex items-start justify-between gap-3 p-3">
          <p className="text-gray-900 italic flex-1" style={{ fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '20px', lineHeight: '140%', letterSpacing: '0%', maxHeight: '84px', overflowY: 'auto', overflowX: 'hidden' }}>
            "{prompt}"
          </p>
          <button
            onClick={handleCopyPrompt}
            className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
            title="Copy prompt"
            style={{ background: 'transparent', outline: 'none', boxShadow: 'none', border: 'none' }}
            {...noOutlineHandlers}
          >
            {showCopiedMessage ? (
              <span className="text-green-600 text-xs font-medium whitespace-nowrap">Copied!</span>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders content-specific information (dimensions for images, video type for videos)
 */
export const renderContentSpecificInfo = (
  isVideoContent: boolean,
  selectedVideo: UserVideo | null | undefined,
  isImageContent: boolean,
  selectedImage: UserImage | null | undefined,
  videoModalEvent: string = 'openVideoModal'
): React.ReactElement | null => {
  // For extended videos, show original video link
  if (isVideoContent && selectedVideo?.original_video_id) {
    return (
      <div className="flex items-start gap-12 text-sm flex-wrap">
        <div>
          <span className="block text-gray-600 font-medium mb-1" style={infoFieldStyles.label}>Extended From</span>
          <button
            onClick={() => {
              globalThis.dispatchEvent(new CustomEvent(videoModalEvent, {
                detail: { videoId: selectedVideo.original_video_id }
              }));
            }}
            className="bg-transparent border-none p-0 m-0 cursor-pointer transition-opacity hover:opacity-70"
            style={{
              ...infoFieldStyles.value,
              background: 'linear-gradient(90deg, #F69A7E 0%, #939BEC 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            title="View original video"
          >
            View Original Video →
          </button>
        </div>
      </div>
    );
  }

  if (isImageContent && selectedImage) {
    return (
      <div className="flex items-start gap-12 text-sm flex-wrap">
        <div>
          <span className="block text-gray-600 font-medium mb-1" style={infoFieldStyles.label}>Dimensions</span>
          <p className="text-gray-900" style={infoFieldStyles.value}>
            {selectedImage.width} × {selectedImage.height}
          </p>
        </div>
      </div>
    );
  }
  
  return null;
};

/**
 * Renders the content information grid (created, generated by, model, size)
 * Used by both VideoDetailsModal and VideoEditModal
 */
export const renderContentInfo = (
  currentItem: UserVideo | UserImage | null | undefined,
  formatDate: (date: string) => string,
  formatFileSize: (size: number) => string,
  defaultModel: string = 'Veo 3 Fast'
): React.ReactElement | null => {
  if (!currentItem) return null;
  
  const shouldTruncate = Boolean(currentItem.user_email);
  
  return (
    <div className="grid grid-cols-2 gap-x-12 gap-y-4 md:flex md:items-start md:gap-12 md:flex-wrap md:justify-between w-full">
      {renderInfoField('Created', formatDate(currentItem.created_at))}
      {renderInfoField(
        'Generated By',
        currentItem.user_email || 'You',
        shouldTruncate,
        shouldTruncate ? currentItem.user_email : undefined
      )}
      {renderInfoField('Model', currentItem.model || defaultModel)}
      {renderInfoField('Size', formatFileSize(currentItem.file_size))}
    </div>
  );
};
